import React, { useState, useEffect, useRef } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { StaffManager } from "@/components/app/StaffManager";
import { PdfFormatSettings } from "@/components/app/PdfFormatSettings";
import { ShiftsSettings } from "@/components/app/ShiftsSettings";
import JornadasAdmin from "@/components/app/JornadasAdmin";
import { Upload, Trash2, KeyRound, Copy, Check, Palette, RotateCcw, Building2, Plus, DollarSign, Users, Clock, Loader2, CheckCircle2, FileText, Wallet } from "lucide-react";
import ThemedBackground, { BG_SPEEDS, DEFAULT_BG_SPEED } from "@/components/app/ThemedBackground";
import { BACKGROUNDS, BG_CATEGORIES, DEFAULT_BG } from "@/lib/backgrounds";
import { PALETTES, BUTTON_SHAPES, TEXT_COLORS, PANEL_COLORS, DEFAULT_PALETTE, DEFAULT_BTN, applyTheme, getPalette } from "@/lib/themes";
import { PLAN_LEVELS, PLAN_KEYS, PLAN_LABELS } from "@/lib/plans";
import { extractLogoColors, removeBackground } from "@/lib/imageColor";
import ContractSign from "@/components/app/ContractSign";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [section, setSection] = useState("colegio");
  const [name, setName] = useState(user?.school_name || "");
  const [lateAfter, setLateAfter] = useState(user?.late_after || "");
  const [lateAfterTeacher, setLateAfterTeacher] = useState(user?.late_after_teacher || "");
  const [exitBefore, setExitBefore] = useState(user?.exit_before || "");
  const [exitBeforeTeacher, setExitBeforeTeacher] = useState(user?.exit_before_teacher || "");
  const syncedRef = useRef(false);
  useEffect(() => {
    // Sincroniza los campos con la sesión una vez que el usuario carga (evita campos vacíos)
    if (user && !syncedRef.current) {
      syncedRef.current = true;
      setName(user.school_name || "");
      setLateAfter(user.late_after || "");
      setLateAfterTeacher(user.late_after_teacher || "");
      setExitBefore(user.exit_before || "");
      setExitBeforeTeacher(user.exit_before_teacher || "");
    }
  }, [user]);
  const [saving, setSaving] = useState(false);
  const [themeBg, setThemeBg] = useState(user?.theme_bg || DEFAULT_BG);
  const [bgCat, setBgCat] = useState(BG_CATEGORIES[0].id);
  const [bgSpeed, setBgSpeed] = useState(user?.theme_bg_speed || DEFAULT_BG_SPEED);
  const [savingTheme, setSavingTheme] = useState(false);
  const [palette, setPalette] = useState(user?.theme_palette || DEFAULT_PALETTE);
  const [customColor, setCustomColor] = useState(user?.theme_custom_color || "#2563EB");
  const [btnShape, setBtnShape] = useState(user?.theme_btn || DEFAULT_BTN);
  const [textColors, setTextColors] = useState({
    title: user?.theme_title_color || "auto",
    body: user?.theme_text_color || "auto",
    btn: user?.theme_btn_text_color || "auto",
  });
  const [panelColor, setPanelColor] = useState(user?.theme_panel_color || "auto");
  const [savingPalette, setSavingPalette] = useState(false);
  const [savingDesign, setSavingDesign] = useState(false);
  const [accounts, setAccounts] = useState(user?.deposit_accounts || []);
  const [maxPay, setMaxPay] = useState(user?.max_payment_amount ? String(user.max_payment_amount) : "");
  const [concepts, setConcepts] = useState(user?.fee_concepts || []);
  const [rules, setRules] = useState(() => ({
    enabled: false, due_day: 10, billing_months: [],
    late_fee_enabled: false, late_fee_mode: "day", late_fee_type: "fixed", late_fee_base: "gross", late_fee_amount: 0, late_fee_cap: 0,
    early_discount_enabled: false, early_discount_day: 5, early_discount_type: "pct", early_discount_value: 0,
    ...(user?.tuition_rules || {}),
  }));
  const [savingRules, setSavingRules] = useState(false);
  const [rulesSaved, setRulesSaved] = useState(false);
  const [savingPlans, setSavingPlans] = useState(false);
  const [plans, setPlans] = useState(() => {
    const pp = user?.payment_plans || {};
    return { basico: pp.basico || "", medio: pp.medio || "", completo: pp.completo || "" };
  });
  const [planLevels, setPlanLevels] = useState(() => {
    const src = user?.payment_plans_levels || {};
    const out = {};
    PLAN_LEVELS.forEach((lv) => {
      const s = src[lv] || {};
      const gradeRows = Object.entries(s.grades || {}).map(([g, gv], i) => ({
        gid: `${lv}-${g}-${i}`, grade: g, basico: gv?.basico || "", medio: gv?.medio || "", completo: gv?.completo || "",
      }));
      out[lv] = { basico: s.basico || "", medio: s.medio || "", completo: s.completo || "", gradeRows };
    });
    return out;
  });
  const addGradeRow = (lv) => setPlanLevels((p) => ({ ...p, [lv]: { ...p[lv], gradeRows: [...(p[lv]?.gradeRows || []), { gid: Math.random().toString(36).slice(2, 10), grade: "", basico: "", medio: "", completo: "" }] } }));
  const updGradeRow = (lv, gid, k, v) => setPlanLevels((p) => ({ ...p, [lv]: { ...p[lv], gradeRows: (p[lv]?.gradeRows || []).map((r) => (r.gid === gid ? { ...r, [k]: v } : r)) } }));
  const delGradeRow = (lv, gid) => setPlanLevels((p) => ({ ...p, [lv]: { ...p[lv], gradeRows: (p[lv]?.gradeRows || []).filter((r) => r.gid !== gid) } }));
  const [savingPay, setSavingPay] = useState(false);
  const [scholarships, setScholarships] = useState(() => (user?.scholarships || []).map((s) => ({ id: s.id, name: s.name, pct: s.pct })));
  const addScholarship = () => setScholarships((s) => [...s, { id: Math.random().toString(36).slice(2, 10), name: "", pct: "" }]);
  const updScholarship = (id, k, v) => setScholarships((s) => s.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const delScholarship = (id) => setScholarships((s) => s.filter((x) => x.id !== id));
  const [watermark, setWatermark] = useState(null);
  const [wmUploading, setWmUploading] = useState(false);
  const wmRef = useRef(null);
  const [loginBg, setLoginBg] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const bgRef = useRef(null);
  const [logo, setLogo] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef(null);
  const [showName, setShowName] = useState(!!user?.logo_show_name);
  const [portalSubtitle, setPortalSubtitle] = useState(user?.portal_subtitle || "");
  const [savingBrand, setSavingBrand] = useState(false);
  useEffect(() => {
    if (user) { setShowName(!!user.logo_show_name); if (user.portal_subtitle != null && portalSubtitle === "") setPortalSubtitle(user.portal_subtitle || ""); }
  }, [user?.logo_show_name, user?.portal_subtitle]); // eslint-disable-line
  const toggleShowName = async (val) => {
    setShowName(val);
    try { await api.put("/auth/profile", { logo_show_name: val }); await checkAuth(); toast.success(val ? "Se mostrará el nombre junto al logo" : "Solo se mostrará el logo"); }
    catch { setShowName(!val); toast.error("No se pudo guardar"); }
  };
  const savePortalSubtitle = async () => {
    setSavingBrand(true);
    try { await api.put("/auth/profile", { portal_subtitle: portalSubtitle.trim() }); await checkAuth(); toast.success("Texto de los portales guardado"); }
    catch { toast.error("No se pudo guardar"); }
    finally { setSavingBrand(false); }
  };

  useEffect(() => {
    if (user?.role === "staff") return;
    api.get("/auth/login-bg").then((r) => setLoginBg(r.data.login_bg_url)).catch(() => {});
    api.get("/auth/school-logo").then((r) => setLogo(r.data.logo_url)).catch(() => {});
    api.get("/auth/watermark").then((r) => setWatermark(r.data.watermark_url)).catch(() => {});
  }, [user?.role]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/school-logo", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setLogo(data.logo_url);
      if (logoRef.current) logoRef.current.value = "";
      toast.success("Logo actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir el logo"); }
    finally { setLogoUploading(false); }
  };
  const removeLogo = async () => {
    try { await api.delete("/auth/school-logo"); setLogo(null); toast.success("Logo eliminado"); }
    catch { toast.error("Error"); }
  };

  const uploadBg = async (file) => {
    if (!file) return;
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/login-bg", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setLoginBg(data.login_bg_url);
      if (bgRef.current) bgRef.current.value = "";
      toast.success("Imagen de login actualizada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { setBgUploading(false); }
  };
  const removeBg = async () => {
    try { await api.delete("/auth/login-bg"); setLoginBg(null); toast.success("Imagen eliminada"); }
    catch { toast.error("Error"); }
  };

  const selectTheme = async (id) => {
    const prev = themeBg;
    setThemeBg(id);
    setSavingTheme(true);
    try {
      await api.put("/auth/profile", { theme_bg: id });
      await checkAuth();
      toast.success("Fondo aplicado");
    } catch { setThemeBg(prev); toast.error("Error"); }
    finally { setSavingTheme(false); }
  };

  const uploadWatermark = async (file) => {
    if (!file) return;
    setWmUploading(true);
    try {
      const processed = await removeBackground(file);
      const fd = new FormData();
      fd.append("file", processed, "watermark.png");
      const { data } = await api.post("/auth/watermark", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setWatermark(data.watermark_url);
      if (wmRef.current) wmRef.current.value = "";
      await checkAuth();
      toast.success("Marca de agua lista (fondo removido)");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir la imagen"); }
    finally { setWmUploading(false); }
  };
  const removeWatermark = async () => {
    try { await api.delete("/auth/watermark"); setWatermark(null); await checkAuth(); toast.success("Marca de agua eliminada"); }
    catch { toast.error("Error"); }
  };

  const wmPreviewUrl = watermark ? `${API}${watermark}` : (logo ? `${API}${logo}` : null);

  const uid = () => Math.random().toString(36).slice(2, 12);
  const addAccount = () => setAccounts((a) => [...a, { id: uid(), bank: "", holder: "", clabe: "", account: "", reference: "" }]);
  const updAccount = (id, k, v) => setAccounts((a) => a.map((x) => x.id === id ? { ...x, [k]: v } : x));
  const delAccount = (id) => setAccounts((a) => a.filter((x) => x.id !== id));
  const saveAccounts = async () => {
    setSavingPay(true);
    try { await api.put("/auth/profile", { deposit_accounts: accounts, max_payment_amount: parseFloat(maxPay) || 0 }); await checkAuth(); toast.success("Cuentas de depósito guardadas"); }
    catch { toast.error("Error"); } finally { setSavingPay(false); }
  };
  const addConcept = () => setConcepts((c) => [...c, { id: uid(), name: "", amount: 0 }]);
  const updConcept = (id, k, v) => setConcepts((c) => c.map((x) => x.id === id ? { ...x, [k]: k === "amount" ? v.replace(/[^0-9.]/g, "") : v } : x));
  const delConcept = (id) => setConcepts((c) => c.filter((x) => x.id !== id));
  const saveConcepts = async () => {
    setSavingPay(true);
    try {
      await api.put("/auth/profile", { fee_concepts: concepts.map((c) => ({ ...c, amount: parseFloat(c.amount) || 0 })) });
      await checkAuth(); toast.success("Precios por concepto guardados");
    } catch { toast.error("Error"); } finally { setSavingPay(false); }
  };
  const updRule = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const billingMonths = Array.isArray(rules.billing_months) ? rules.billing_months : [];
  const toggleMonth = (m) => updRule("billing_months", billingMonths.includes(m) ? billingMonths.filter((x) => x !== m) : [...billingMonths, m].sort((a, b) => a - b));
  const setRange = (from, to) => {
    const f = parseInt(from, 10), t = parseInt(to, 10);
    if (!f || !t) return;
    const arr = [];
    let cur = f;
    for (let i = 0; i < 12; i++) { arr.push(cur); if (cur === t) break; cur = cur === 12 ? 1 : cur + 1; }
    updRule("billing_months", arr.sort((a, b) => a - b));
  };
  const savePlans = async () => {
    setSavingPlans(true);
    try {
      const num = (v) => parseFloat(v) || 0;
      const levels = {};
      PLAN_LEVELS.forEach((lv) => {
        const lvl = planLevels[lv] || {};
        const grades = {};
        (lvl.gradeRows || []).forEach((r) => {
          const g = String(r.grade || "").trim();
          if (!g) return;
          const gvals = { basico: num(r.basico), medio: num(r.medio), completo: num(r.completo) };
          if (Object.values(gvals).some((v) => v > 0)) grades[g] = gvals;
        });
        levels[lv] = { basico: num(lvl.basico), medio: num(lvl.medio), completo: num(lvl.completo), grades };
      });
      await api.put("/auth/profile", {
        payment_plans: { basico: num(plans.basico), medio: num(plans.medio), completo: num(plans.completo) },
        payment_plans_levels: levels,
        scholarships: scholarships.filter((s) => (s.name || "").trim()).map((s) => ({ id: s.id, name: s.name.trim(), pct: parseFloat(s.pct) || 0 })),
      });
      await checkAuth(); toast.success("Planes de pago y becas guardados");
    } catch { toast.error("Error"); } finally { setSavingPlans(false); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    const body = { tuition_rules: {
        enabled: !!rules.enabled,
        due_day: Math.min(28, Math.max(1, parseInt(rules.due_day, 10) || 10)),
        billing_months: Array.isArray(rules.billing_months) ? rules.billing_months : [],
        late_fee_enabled: !!rules.late_fee_enabled,
        late_fee_mode: ["day", "week", "month", "flat"].includes(rules.late_fee_mode) ? rules.late_fee_mode : "day",
        late_fee_base: rules.late_fee_base === "net" ? "net" : "gross",
        late_fee_type: rules.late_fee_type === "pct" ? "pct" : "fixed",
        late_fee_amount: parseFloat(rules.late_fee_amount) || 0,
        late_fee_cap: parseFloat(rules.late_fee_cap) || 0,
        early_discount_enabled: !!rules.early_discount_enabled,
        early_discount_day: Math.min(28, Math.max(1, parseInt(rules.early_discount_day, 10) || 5)),
        early_discount_type: rules.early_discount_type === "fixed" ? "fixed" : "pct",
        early_discount_value: parseFloat(rules.early_discount_value) || 0,
    } };
    // Guardado resiliente: reintenta ante fallos transitorios de red/servidor (ej. 520/timeout del
    // proxy en producción) para que NO se muestre "Error" cuando el guardado puede completarse.
    let lastErr;
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await api.put("/auth/profile", body, { timeout: 30000 });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          const st = e?.response?.status;
          // No reintentar en errores de validación/permiso (4xx que no sean 408/429)
          if (st && st < 500 && st !== 408 && st !== 429) break;
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        }
      }
      if (lastErr) throw lastErr;
      toast.success("Reglas de colegiatura guardadas");
      setRulesSaved(true);
      setTimeout(() => setRulesSaved(false), 3500);
      try { await checkAuth(); } catch { /* refresco de sesión no crítico: las reglas ya se guardaron */ }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || (e?.message?.includes("Network") ? "Sin conexión con el servidor. Revisa tu internet e intenta de nuevo." : "No se pudieron guardar las reglas. Intenta de nuevo."));
    } finally { setSavingRules(false); }
  };

  const selectPalette = async (id) => {
    const prev = palette;
    setPalette(id);
    applyTheme(id, btnShape, textColors, user?.theme_logo_colors, panelColor, customColor);
    setSavingPalette(true);
    try {
      await api.put("/auth/profile", { theme_palette: id });
      await checkAuth();
      toast.success("Paleta aplicada");
    } catch { setPalette(prev); applyTheme(prev, btnShape, textColors, user?.theme_logo_colors, panelColor, customColor); toast.error("Error"); }
    finally { setSavingPalette(false); }
  };

  const applyCustomColor = async (hex) => {
    const prevP = palette; const prevC = customColor;
    setCustomColor(hex); setPalette("custom");
    applyTheme("custom", btnShape, textColors, user?.theme_logo_colors, panelColor, hex);
    try {
      await api.put("/auth/profile", { theme_palette: "custom", theme_custom_color: hex });
      await checkAuth();
      toast.success("Color de marca aplicado");
    } catch { setCustomColor(prevC); setPalette(prevP); applyTheme(prevP, btnShape, textColors, user?.theme_logo_colors, panelColor, prevC); toast.error("Error"); }
  };

  const selectBtnShape = async (id) => {
    const prev = btnShape;
    setBtnShape(id);
    applyTheme(palette, id, textColors, user?.theme_logo_colors, panelColor, customColor);
    setSavingPalette(true);
    try {
      await api.put("/auth/profile", { theme_btn: id });
      await checkAuth();
      toast.success("Estilo de botón aplicado");
    } catch { setBtnShape(prev); applyTheme(palette, prev, textColors, user?.theme_logo_colors, panelColor, customColor); toast.error("Error"); }
    finally { setSavingPalette(false); }
  };
  const selectTextColor = async (kind, id) => {
    const prev = textColors;
    const next = { ...textColors, [kind]: id };
    setTextColors(next);
    applyTheme(palette, btnShape, next, user?.theme_logo_colors, panelColor, customColor);
    setSavingPalette(true);
    const fieldMap = { title: "theme_title_color", body: "theme_text_color", btn: "theme_btn_text_color" };
    try {
      await api.put("/auth/profile", { [fieldMap[kind]]: id });
      await checkAuth();
      toast.success("Color de texto aplicado");
    } catch { setTextColors(prev); applyTheme(palette, btnShape, prev, user?.theme_logo_colors, panelColor, customColor); toast.error("Error"); }
    finally { setSavingPalette(false); }
  };
  const selectPanelColor = async (id) => {
    const prev = panelColor;
    setPanelColor(id);
    applyTheme(palette, btnShape, textColors, user?.theme_logo_colors, id, customColor);
    setSavingPalette(true);
    try {
      await api.put("/auth/profile", { theme_panel_color: id });
      await checkAuth();
      toast.success("Color de paneles aplicado");
    } catch { setPanelColor(prev); applyTheme(palette, btnShape, textColors, user?.theme_logo_colors, prev, customColor); toast.error("Error"); }
    finally { setSavingPalette(false); }
  };
  const applyLogoColors = async () => {
    if (!logo) { toast.error("Primero sube el logo del colegio (más abajo)"); return; }
    setSavingPalette(true);
    try {
      const { data } = await api.get(logo, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const colors = await extractLogoColors(url);
      URL.revokeObjectURL(url);
      setPalette("logo");
      applyTheme("logo", btnShape, textColors, colors, panelColor);
      await api.put("/auth/profile", { theme_palette: "logo", theme_logo_colors: colors });
      await checkAuth();
      toast.success("Paleta creada desde tu logo");
    } catch { toast.error("No se pudieron extraer los colores del logo"); }
    finally { setSavingPalette(false); }
  };

  const selectBgSpeed = async (id) => {
    const prev = bgSpeed;
    setBgSpeed(id);
    setSavingTheme(true);
    try {
      await api.put("/auth/profile", { theme_bg_speed: id });
      await checkAuth();
      toast.success("Velocidad aplicada");
    } catch { setBgSpeed(prev); toast.error("Error"); }
    finally { setSavingTheme(false); }
  };

  const resetDesign = async () => {
    setSavingPalette(true);
    try {
      await api.put("/auth/profile", {
        theme_bg: DEFAULT_BG, theme_bg_speed: DEFAULT_BG_SPEED,
        theme_palette: DEFAULT_PALETTE, theme_btn: DEFAULT_BTN,
        theme_title_color: "auto", theme_text_color: "auto", theme_btn_text_color: "auto",
        theme_panel_color: "auto",
      });
      setThemeBg(DEFAULT_BG); setBgSpeed(DEFAULT_BG_SPEED);
      setPalette(DEFAULT_PALETTE); setBtnShape(DEFAULT_BTN);
      const tc = { title: "auto", body: "auto", btn: "auto" };
      setTextColors(tc); setPanelColor("auto");
      applyTheme(DEFAULT_PALETTE, DEFAULT_BTN, tc, null, "auto");
      await checkAuth();
      toast.success("Diseño restablecido a ZAVIA");
    } catch { toast.error("Error"); }
    finally { setSavingPalette(false); }
  };

  const saveDesignConfig = async () => {
    setSavingDesign(true);
    try {
      await api.put("/auth/profile", {
        theme_palette: palette, theme_custom_color: customColor,
        theme_btn: btnShape,
        theme_title_color: textColors.title, theme_text_color: textColors.body, theme_btn_text_color: textColors.btn,
        theme_panel_color: panelColor,
        theme_bg: themeBg, theme_bg_speed: bgSpeed,
        ...(user?.theme_logo_colors ? { theme_logo_colors: user.theme_logo_colors } : {}),
      });
      await checkAuth();
      toast.success("Configuración guardada. Se aplicará en todos tus portales al iniciar sesión.");
    } catch { toast.error("No se pudo guardar la configuración"); }
    finally { setSavingDesign(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", { school_name: name, late_after: lateAfter, late_after_teacher: lateAfterTeacher, exit_before: exitBefore, exit_before_teacher: exitBeforeTeacher });
      await checkAuth();
      toast.success("Guardado. El nombre del colegio se actualizó en el panel, PDFs, credenciales y archivos.");
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  if (user?.role === "staff") return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
          <span className="w-8 h-px bg-navy" /> Configuración
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">Ajustes</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="settings-tabs">
        {[
          { id: "colegio", label: "Colegio y horarios", icon: Clock },
          { id: "diseno", label: "Diseño de página", icon: Palette },
          { id: "pdf", label: "Formato de PDF", icon: FileText },
          { id: "cobranza", label: "Cobranza y pagos", icon: DollarSign },
          { id: "padres", label: "Acceso de padres", icon: KeyRound },
          { id: "staff", label: "Correos administrativos", icon: Users },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            data-testid={`settings-tab-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${section === id ? "bg-navy text-white" : "bg-white border border-nardo-200 text-nardo-600 hover:text-navy hover:border-navy"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {section === "colegio" && (<>
      <ContractSign />
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Colegio</h2>
        <p className="text-sm text-nardo-500 mb-6">Este nombre aparece en reportes PDF y en el panel.</p>
        <form onSubmit={save} className="space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nombre del colegio</label>
            <input
              data-testid="school-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
              placeholder="Mi Colegio"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Hora límite de entrada de alumno (retardo)</label>
            <input
              type="time"
              data-testid="late-after-input"
              value={lateAfter}
              onChange={(e) => setLateAfter(e.target.value)}
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
            />
            <p className="text-[11px] text-nardo-400 mt-1">Las entradas de <strong>alumnos</strong> después de esta hora se marcan como <span className="text-orange-600 font-medium">retardo</span> (naranja). Déjalo vacío para no marcar retardos.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Hora límite de entrada de docente (retardo)</label>
            <input
              type="time"
              data-testid="late-after-teacher-input"
              value={lateAfterTeacher}
              onChange={(e) => setLateAfterTeacher(e.target.value)}
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
            />
            <p className="text-[11px] text-nardo-400 mt-1">Las entradas de <strong>docentes</strong> después de esta hora se marcan como <span className="text-orange-600 font-medium">retardo</span> (naranja). Déjalo vacío para no marcar retardos.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Hora de salida mínima de alumno (salida anticipada)</label>
            <input
              type="time"
              data-testid="exit-before-input"
              value={exitBefore}
              onChange={(e) => setExitBefore(e.target.value)}
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
            />
            <p className="text-[11px] text-nardo-400 mt-1">Las salidas de <strong>alumnos</strong> antes de esta hora se marcan como <span className="text-violet-600 font-medium">salida anticipada</span> (morado). Déjalo vacío para no marcar.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Hora de salida mínima de docente (salida anticipada)</label>
            <input
              type="time"
              data-testid="exit-before-teacher-input"
              value={exitBeforeTeacher}
              onChange={(e) => setExitBeforeTeacher(e.target.value)}
              className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
            />
            <p className="text-[11px] text-nardo-400 mt-1">Las salidas de <strong>docentes</strong> antes de esta hora se marcan como <span className="text-violet-600 font-medium">salida anticipada</span> (morado). Déjalo vacío para no marcar.</p>
          </div>
          <button type="submit" disabled={saving} data-testid="save-settings-button"
            className="bg-navy hover:bg-navy-light text-white px-6 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </form>

        <ShiftsSettings user={user} checkAuth={checkAuth} />

        <div className="mt-10 pt-6 border-t border-nardo-200" data-testid="settings-jornadas">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-5 h-5 text-navy" /><h2 className="font-display font-semibold text-xl text-navy">Jornadas laborales (docentes y personal)</h2></div>
          <p className="text-sm text-nardo-500 mb-4">Define los movimientos y horarios del día (entrada, desayuno, comida, salida). Luego asígnalos por persona en <strong>Docentes → Editar</strong>.</p>
          <JornadasAdmin showBoard={false} />
        </div>

        <div className="mt-10 pt-6 border-t border-nardo-200">
          <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-2">Cuenta</div>
          <div className="text-sm">
            <div><span className="text-nardo-500">Email:</span> <span className="text-navy font-medium">{user?.email}</span></div>
            <div className="mt-1"><span className="text-nardo-500">Nombre:</span> <span className="text-navy font-medium">{user?.name}</span></div>
          </div>
        </div>
      </div>

      </>)}

      {section === "diseno" && (<>
      <div className="sticky top-2 z-20 flex items-center justify-between gap-3 bg-navy text-white rounded-md px-4 py-3 shadow-lg mb-4" data-testid="design-save-bar">
        <div className="text-sm">
          <div className="font-semibold">Guarda tu diseño</div>
          <div className="text-white/70 text-[11px]">Colores, logo, fondo e imagen quedarán fijos para todos tus portales al iniciar sesión.</div>
        </div>
        <button type="button" onClick={saveDesignConfig} disabled={savingDesign} data-testid="save-design-config-btn"
          className="inline-flex items-center gap-2 bg-white text-navy hover:bg-nardo-100 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60 shrink-0">
          {savingDesign ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar configuración
        </button>
      </div>
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative" data-testid="login-bg-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Imagen de fondo del login</h2>
        <p className="text-sm text-nardo-500 mb-5">Se muestra en la pantalla de inicio de sesión cuando un usuario valida el <strong>código de acceso</strong> de tu colegio. Usa una imagen <strong>horizontal (apaisada)</strong>, mínimo 1600×900 px.</p>
        <div className="rounded-lg overflow-hidden border border-nardo-200 bg-nardo-50 aspect-video flex items-center justify-center mb-4" data-testid="login-bg-preview">
          {loginBg ? (
            <img src={`${API}${loginBg}`} alt="Fondo de login" className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm text-nardo-400">Sin imagen personalizada (se usa la imagen por defecto)</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => bgRef.current?.click()} disabled={bgUploading} data-testid="login-bg-upload" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"><Upload className="w-4 h-4" /> {bgUploading ? "Subiendo…" : (loginBg ? "Cambiar imagen" : "Subir imagen")}</button>
          {loginBg && <button type="button" onClick={removeBg} data-testid="login-bg-remove" className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-600 hover:text-destructive hover:border-destructive px-4 py-2 rounded-md text-sm font-medium transition-colors"><Trash2 className="w-4 h-4" /> Quitar</button>}
          <input ref={bgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} />
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="school-logo-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Logo del colegio</h2>
        <p className="text-sm text-nardo-500 mb-5">Se muestra en la pantalla de login (sobre el panel) cuando el usuario valida el <strong>código de acceso</strong>. Usa PNG con fondo transparente si es posible.</p>
        <div className="rounded-lg overflow-hidden border border-nardo-200 bg-nardo-100 h-28 flex items-center justify-center mb-4" data-testid="school-logo-preview">
          {logo ? (
            <img src={`${API}${logo}`} alt="Logo del colegio" className="h-20 w-auto max-w-[220px] object-contain" />
          ) : (
            <span className="text-sm text-nardo-400">Sin logo (se usa el logo de ZAVIA)</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => logoRef.current?.click()} disabled={logoUploading} data-testid="school-logo-upload" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"><Upload className="w-4 h-4" /> {logoUploading ? "Subiendo…" : (logo ? "Cambiar logo" : "Subir logo")}</button>
          {logo && <button type="button" onClick={removeLogo} data-testid="school-logo-remove" className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-600 hover:text-destructive hover:border-destructive px-4 py-2 rounded-md text-sm font-medium transition-colors"><Trash2 className="w-4 h-4" /> Quitar</button>}
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
        </div>

        {/* Mostrar nombre junto al logo */}
        <label className="flex items-center gap-3 mt-5 pt-4 border-t border-nardo-100 cursor-pointer" data-testid="logo-show-name-row">
          <input type="checkbox" checked={showName} onChange={(e) => toggleShowName(e.target.checked)} data-testid="logo-show-name-toggle" className="w-5 h-5 accent-navy" />
          <span className="text-sm text-navy font-medium">Mostrar el nombre del colegio junto al logo</span>
        </label>
        <p className="text-[11px] text-nardo-400 mt-1 ml-8">Cuando subes un logo, actívalo para que también aparezca el nombre del colegio a un lado en el panel.</p>

        {/* Texto bajo el título de los portales */}
        <div className="mt-5 pt-4 border-t border-nardo-100" data-testid="portal-subtitle-row">
          <label className="text-sm text-navy font-medium">Texto bajo el título de los portales</label>
          <p className="text-[11px] text-nardo-400 mt-0.5 mb-2">Aparece debajo de “Portal de Padres”, “Portal del Alumno” y “Portal del docente”. Ej.: <strong>Colegio Pitágoras</strong>. Si lo dejas vacío, se muestra “ZAVIA”.</p>
          <div className="flex flex-wrap gap-2">
            <input value={portalSubtitle} onChange={(e) => setPortalSubtitle(e.target.value)} maxLength={60} placeholder="Colegio Pitágoras" data-testid="portal-subtitle-input" className="flex-1 min-w-[200px] border border-nardo-200 rounded-md px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
            <button type="button" onClick={savePortalSubtitle} disabled={savingBrand} data-testid="portal-subtitle-save" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">{savingBrand ? "Guardando…" : "Guardar"}</button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="theme-bg-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Fondos animados</h2>
        <p className="text-sm text-nardo-500 mb-5">Elige un fondo en movimiento para <strong>toda la cuenta</strong> (app de usuario y panel de administrador). Más de 50 estilos: líneas, círculos, útiles escolares, colores que cambian, nubes, ejecutivos y más.</p>

        {/* Tabs de categoría */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="bg-category-tabs">
          {BG_CATEGORIES.map((c) => (
            <button key={c.id} type="button" data-testid={`bg-cat-${c.id}`}
              onClick={() => setBgCat(c.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${bgCat === c.id ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600 hover:bg-nardo-200"}`}>
              {c.name}
            </button>
          ))}
        </div>

        {/* Velocidad del movimiento */}
        <div className="mb-5" data-testid="bg-speed-group">
          <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-2">Velocidad del movimiento</div>
          <div className="inline-flex rounded-lg border border-nardo-200 overflow-hidden">
            {BG_SPEEDS.map((s) => (
              <button key={s.id} type="button" data-testid={`bg-speed-${s.id}`}
                onClick={() => selectBgSpeed(s.id)} disabled={savingTheme}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${bgSpeed === s.id ? "bg-navy text-white" : "bg-white text-nardo-600 hover:bg-nardo-100"}`}>
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {bgCat === "marca" && (
          <div className="mb-6 bg-nardo-50 border border-nardo-200 rounded-lg p-4" data-testid="watermark-uploader">
            <div className="text-sm font-medium text-navy mb-1">Tu logo como marca de agua</div>
            <p className="text-[12px] text-nardo-500 mb-3">Sube el logo que se mostrará flotando/repetido en el fondo. Si no subes uno, se usa el logo del colegio.</p>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-md bg-white border border-nardo-200 flex items-center justify-center overflow-hidden shrink-0">
                {wmPreviewUrl ? <img src={wmPreviewUrl} alt="" className="max-w-[80%] max-h-[80%] object-contain" /> : <span className="text-[10px] text-nardo-400 text-center px-1">Sin logo</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => wmRef.current?.click()} disabled={wmUploading} data-testid="watermark-upload" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"><Upload className="w-4 h-4" /> {wmUploading ? "Subiendo…" : (watermark ? "Cambiar logo" : "Subir logo")}</button>
                {watermark && <button type="button" onClick={removeWatermark} data-testid="watermark-remove" className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-600 hover:text-destructive hover:border-destructive px-4 py-2 rounded-md text-sm font-medium transition-colors"><Trash2 className="w-4 h-4" /> Quitar</button>}
                <input ref={wmRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadWatermark(e.target.files?.[0])} />
              </div>
            </div>
          </div>
        )}

        {/* Galería */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="bg-gallery">
          {BACKGROUNDS.filter((b) => b.category === bgCat).map((b) => {
            const active = themeBg === b.id;
            return (
              <button key={b.id} type="button" data-testid={`bg-option-${b.id}`}
                onClick={() => selectTheme(b.id)} disabled={savingTheme}
                className={`relative rounded-lg overflow-hidden border-2 transition-all text-left ${active ? "border-navy ring-2 ring-navy/30" : "border-nardo-200 hover:border-navy/50"} disabled:opacity-70`}>
                <div className="relative h-20 w-full">
                  <ThemedBackground bgId={b.id} watermarkUrl={wmPreviewUrl} speed={bgSpeed} preview />
                </div>
                {active && <span className="absolute top-1.5 right-1.5 z-10 bg-navy text-white rounded-full p-1"><Check className="w-3 h-3" /></span>}
                <div className="px-2 py-1.5 bg-white relative z-10">
                  <div className="text-[11px] font-medium text-navy truncate">{b.name}</div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-nardo-400 mt-4">Se aplica al instante en toda la app. El contenido siempre permanece legible sobre el fondo.</p>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="theme-palette-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h2 className="font-display font-semibold text-xl text-navy mb-1">Temas de color, botones y textos</h2>
          <button type="button" data-testid="reset-design-btn" onClick={resetDesign} disabled={savingPalette}
            className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-600 hover:text-navy hover:border-navy px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-60">
            <RotateCcw className="w-3.5 h-3.5" /> Restablecer diseño
          </button>
        </div>
        <p className="text-sm text-nardo-500 mb-5">Elige la paleta de color de tu marca y el estilo de los botones. Se aplica a <strong>toda la cuenta</strong> (usuario y administrador).</p>

        <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-3">Paleta de color</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="palette-grid">
          {PALETTES.map((p) => {
            const active = palette === p.id;
            return (
              <button key={p.id} type="button" data-testid={`palette-${p.id}`}
                onClick={() => selectPalette(p.id)} disabled={savingPalette}
                className={`rounded-lg border-2 p-2 transition-all ${active ? "border-navy ring-2 ring-navy/30" : "border-nardo-200 hover:border-navy/40"} disabled:opacity-70`}>
                <div className="flex gap-1 mb-2">
                  <span className="flex-1 h-8 rounded" style={{ background: p.ink }} />
                  <span className="flex-1 h-8 rounded" style={{ background: p.brand }} />
                  <span className="flex-1 h-8 rounded" style={{ background: p.light }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-navy truncate">{p.name}</span>
                  {active && <Check className="w-3.5 h-3.5 text-navy shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mb-8">
          <button type="button" data-testid="palette-logo-btn" onClick={applyLogoColors} disabled={savingPalette}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${palette === "logo" ? "border-navy ring-2 ring-navy/30 bg-nardo-50" : "border-nardo-200 hover:border-navy/40"} disabled:opacity-60`}>
            <Palette className="w-4 h-4 text-navy" />
            Usar los colores de mi logo
            {palette === "logo" && <Check className="w-4 h-4 text-navy" />}
          </button>
          <p className="text-[11px] text-nardo-400 mt-2">Genera una paleta automáticamente a partir del logo del colegio (súbelo más abajo en “Logo del colegio”).</p>
        </div>

        <div className="mb-8 p-4 rounded-lg border-2 border-dashed border-nardo-200" data-testid="custom-color-box">
          <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-2">Color personalizado (cualquier color)</div>
          <div className="flex flex-wrap items-center gap-3">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(customColor) ? customColor : "#2563EB"} onChange={(e) => setCustomColor(e.target.value)} data-testid="custom-color-input" className="w-12 h-10 rounded-md border border-nardo-200 cursor-pointer bg-white p-0.5" />
            <input type="text" value={customColor} onChange={(e) => setCustomColor(e.target.value)} data-testid="custom-color-hex" placeholder="#2563EB" className="w-28 border border-nardo-200 rounded-md px-2 py-2 text-sm font-mono" />
            <button type="button" onClick={() => applyCustomColor(customColor)} disabled={savingPalette || !/^#[0-9a-fA-F]{6}$/.test(customColor)} data-testid="custom-color-apply"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 ${palette === "custom" ? "ring-2 ring-offset-2 ring-navy" : ""}`} style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(customColor) ? customColor : "#2563EB" }}>
              {palette === "custom" ? <Check className="w-4 h-4" /> : <Palette className="w-4 h-4" />}
              Guardar cambios
            </button>
          </div>
          <p className="text-[11px] text-nardo-400 mt-2">Elige el color exacto de tu colegio. Se aplica al panel, al Portal de Padres y al Portal de Docentes.</p>
        </div>

        <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-3">Estilo de botones</div>
        <div className="flex flex-wrap gap-3 mb-6" data-testid="btn-shape-group">
          {BUTTON_SHAPES.map((s) => {
            const active = btnShape === s.id;
            const shapeCls = s.id === "pill" ? "rounded-full" : s.id === "square" ? "rounded-none" : "rounded-lg";
            return (
              <button key={s.id} type="button" data-testid={`btn-shape-${s.id}`}
                onClick={() => selectBtnShape(s.id)} disabled={savingPalette}
                className={`flex flex-col items-center gap-2 px-4 py-3 border-2 transition-all rounded-lg ${active ? "border-navy ring-2 ring-navy/30" : "border-nardo-200 hover:border-navy/40"}`}>
                <span className={`px-4 py-1.5 bg-navy text-white text-xs font-medium ${shapeCls}`}>Botón</span>
                <span className="text-[11px] text-navy">{s.name}</span>
              </button>
            );
          })}
        </div>

        {/* Colores de texto */}
        <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-3">Colores de texto</div>
        <div className="space-y-4 mb-6" data-testid="text-colors-group">
          {[
            { kind: "title", label: "Títulos" },
            { kind: "body", label: "Textos del sistema" },
            { kind: "btn", label: "Texto de botones" },
          ].map(({ kind, label }) => (
            <div key={kind} className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-navy w-40 shrink-0">{label}</span>
              <div className="flex flex-wrap gap-1.5" data-testid={`text-color-${kind}`}>
                {TEXT_COLORS.map((c) => {
                  const active = textColors[kind] === c.id;
                  return (
                    <button key={c.id} type="button" title={c.name} data-testid={`text-color-${kind}-${c.id}`}
                      onClick={() => selectTextColor(kind, c.id)} disabled={savingPalette}
                      className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${active ? "border-navy ring-2 ring-navy/30" : "border-nardo-200 hover:border-navy/40"}`}
                      style={{ background: c.hex || "#FFFFFF" }}>
                      {c.id === "auto" && <span className="text-[8px] font-bold text-nardo-500">A</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Color de paneles */}
        <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-3">Color de paneles / tarjetas</div>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="panel-color-group">
          {PANEL_COLORS.map((c) => {
            const active = panelColor === c.id;
            return (
              <button key={c.id} type="button" title={c.name} data-testid={`panel-color-${c.id}`}
                onClick={() => selectPanelColor(c.id)} disabled={savingPalette}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border-2 transition-all ${active ? "border-navy ring-2 ring-navy/30" : "border-nardo-200 hover:border-navy/40"}`}>
                <span className="w-6 h-6 rounded-full border border-nardo-200" style={{ background: c.hex || "#FFFFFF" }} />
                <span className="text-[11px] text-navy">{c.name}</span>
                {active && <Check className="w-3.5 h-3.5 text-navy" />}
              </button>
            );
          })}
        </div>

        {/* Vista previa */}
        <div className="rounded-lg border border-nardo-200 bg-nardo-50 p-4" data-testid="theme-preview">
          <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 mb-3">Vista previa</div>
          <h3 className="font-display font-bold text-lg text-navy mb-1">Título de ejemplo</h3>
          <p className="text-sm text-navy mb-3">Este es un texto del sistema de ejemplo para ver el color aplicado.</p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="bg-navy hover:bg-navy-light text-white px-5 py-2 text-sm font-medium transition-colors">Acción principal</button>
            <button type="button" className="border-2 border-navy text-navy px-5 py-2 text-sm font-medium">Secundario</button>
            <span className="inline-flex items-center px-3 py-1 bg-navy/10 text-navy text-xs font-medium rounded-full">Etiqueta</span>
          </div>
        </div>
      </div>


      </>)}

      {section === "pdf" && <PdfFormatSettings user={user} checkAuth={checkAuth} />}


      {section === "cobranza" && (<>
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative" data-testid="tuition-rules-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h2 className="font-display font-semibold text-xl text-navy mb-1">Reglas de colegiatura</h2>
        <p className="text-sm text-nardo-500 mb-5">Se aplican automáticamente a <strong>todos los alumnos</strong> (solo Colegiatura). Se reflejan en el Portal de Padres, en el sistema y en Cobranza → Adeudos.</p>

        <label className="flex items-center gap-2 mb-5 cursor-pointer">
          <input type="checkbox" checked={!!rules.enabled} onChange={(e) => updRule("enabled", e.target.checked)} data-testid="rules-enabled" className="w-4 h-4 accent-navy" />
          <span className="text-sm font-medium text-navy">Activar reglas automáticas de colegiatura</span>
        </label>

        <div className={`space-y-6 ${rules.enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Fecha límite de pago (día del mes)</label>
            <input type="number" min={1} max={28} value={rules.due_day} onChange={(e) => updRule("due_day", e.target.value)} data-testid="rules-due-day" className="w-24 mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm" />
            <p className="text-[11px] text-nardo-400 mt-1">Ej. día 10: se repite cada mes de cobro.</p>
          </div>

          <div className="border-t border-nardo-100 pt-4" data-testid="rules-billing-months">
            <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Periodo de cobro (meses que se cobran)</label>
            <p className="text-[11px] text-nardo-400 mt-1 mb-3">Elige de qué mes a qué mes se cobra colegiatura (ej. Septiembre a Junio). Los meses que no marques <strong>no generan cobro</strong>. Déjalo vacío para cobrar los 12 meses.</p>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">De</label>
                <select id="bm-from" data-testid="rules-months-from" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                  {MONTHS_ES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">a</label>
                <select id="bm-to" data-testid="rules-months-to" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white" defaultValue={6}>
                  {MONTHS_ES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <button type="button" data-testid="rules-months-apply-range"
                onClick={() => setRange(document.getElementById("bm-from").value, document.getElementById("bm-to").value)}
                className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-md text-sm font-semibold transition-colors">Aplicar periodo</button>
              <button type="button" data-testid="rules-months-all" onClick={() => updRule("billing_months", [])} className="text-xs text-nardo-500 hover:text-navy underline">Cobrar los 12 meses</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {MONTHS_ES.map((m, i) => {
                const mn = i + 1;
                const on = billingMonths.length === 0 || billingMonths.includes(mn);
                return (
                  <button key={mn} type="button" onClick={() => toggleMonth(mn)} data-testid={`rules-month-${mn}`}
                    className={`w-12 py-1.5 rounded-md text-xs font-semibold border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-nardo-400 border-nardo-200 hover:border-navy"}`}>{m}</button>
                );
              })}
            </div>
            <p className="text-[11px] text-nardo-400 mt-2">{billingMonths.length === 0 ? "Se cobran los 12 meses." : `Se cobran ${billingMonths.length} mes(es): ${billingMonths.map((x) => MONTHS_ES[x - 1]).join(", ")}.`}</p>
          </div>

          <div className="border-t border-nardo-100 pt-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={!!rules.late_fee_enabled} onChange={(e) => updRule("late_fee_enabled", e.target.checked)} data-testid="rules-latefee-enabled" className="w-4 h-4 accent-navy" />
              <span className="text-sm font-medium text-navy">Cobrar recargo por mora (después de la fecha límite)</span>
            </label>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Cobrar por</label>
                <select value={rules.late_fee_mode} onChange={(e) => updRule("late_fee_mode", e.target.value)} data-testid="rules-latefee-mode" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="day">Día de atraso</option>
                  <option value="week">Semana de atraso</option>
                  <option value="month">Mes de atraso</option>
                  <option value="flat">Una sola vez (al vencer)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Tipo</label>
                <select value={rules.late_fee_type} onChange={(e) => updRule("late_fee_type", e.target.value)} data-testid="rules-latefee-type" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="pct">Porcentaje (%)</option>
                  <option value="fixed">Monto fijo ($)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">{rules.late_fee_type === "pct" ? "Porcentaje" : "Monto (MXN)"}</label>
                <input type="text" inputMode="decimal" value={rules.late_fee_amount} onChange={(e) => updRule("late_fee_amount", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="rules-latefee-amount" className="w-28 mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="0" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Tope máx. (0 = sin tope)</label>
                <input type="text" inputMode="decimal" value={rules.late_fee_cap} onChange={(e) => updRule("late_fee_cap", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="rules-latefee-cap" className="w-28 mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="0" />
              </div>
              {rules.late_fee_type === "pct" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Calcular % sobre</label>
                  <select value={rules.late_fee_base || "gross"} onChange={(e) => updRule("late_fee_base", e.target.value)} data-testid="rules-latefee-base" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                    <option value="gross">Colegiatura original (antes de beca)</option>
                    <option value="net">Monto con beca aplicada</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-nardo-100 pt-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={!!rules.early_discount_enabled} onChange={(e) => updRule("early_discount_enabled", e.target.checked)} data-testid="rules-discount-enabled" className="w-4 h-4 accent-navy" />
              <span className="text-sm font-medium text-navy">Descuento por pago anticipado</span>
            </label>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Paga en/antes del día</label>
                <input type="number" min={1} max={28} value={rules.early_discount_day} onChange={(e) => updRule("early_discount_day", e.target.value)} data-testid="rules-discount-day" className="w-24 mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Tipo</label>
                <select value={rules.early_discount_type} onChange={(e) => updRule("early_discount_type", e.target.value)} data-testid="rules-discount-type" className="mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="pct">Porcentaje (%)</option>
                  <option value="fixed">Monto fijo ($)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">{rules.early_discount_type === "pct" ? "Porcentaje" : "Monto (MXN)"}</label>
                <input type="text" inputMode="decimal" value={rules.early_discount_value} onChange={(e) => updRule("early_discount_value", e.target.value.replace(/[^0-9.]/g, ""))} data-testid="rules-discount-value" className="w-28 mt-1 block border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="0" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={saveRules} disabled={savingRules} data-testid="rules-save" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
            {savingRules && <Loader2 className="w-4 h-4 animate-spin" />}
            {savingRules ? "Guardando…" : "Guardar reglas"}
          </button>
          {savingRules && <span className="inline-flex items-center gap-1.5 text-sm text-nardo-500" data-testid="rules-save-status"><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</span>}
          {!savingRules && rulesSaved && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-testid="rules-save-status"><CheckCircle2 className="w-4 h-4" /> Guardado ✓</span>}
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="payment-plans-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex items-center gap-2 mb-1"><Wallet className="w-5 h-5 text-navy" /><h2 className="font-display font-semibold text-xl text-navy">Planes de pago por nivel</h2></div>
        <p className="text-sm text-nardo-500 mb-5">Define el importe mensual de cada plan por nivel educativo y, si quieres, por <b>grado</b>. Al asignar un plan a un alumno se toma el importe de <b>su grado</b> (si existe), luego el de <b>su nivel</b>, y si ambos quedan en 0, el importe <b>General</b>.</p>
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-navy font-semibold mb-2">General (predeterminado)</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {PLAN_KEYS.map((k) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">{PLAN_LABELS[k]}</label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-nardo-400 text-sm">$</span>
                  <input type="number" min={0} step="0.01" value={plans[k]} onChange={(e) => setPlans((p) => ({ ...p, [k]: e.target.value }))} data-testid={`plan-${k}`} placeholder="0.00" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-5">
          {PLAN_LEVELS.map((lv) => (
            <div key={lv} className="border-t border-nardo-100 pt-4" data-testid={`plan-level-${lv.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-semibold mb-2">{lv}</div>
              <div className="grid sm:grid-cols-3 gap-4">
                {PLAN_KEYS.map((k) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">{PLAN_LABELS[k]}</label>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-nardo-400 text-sm">$</span>
                      <input type="number" min={0} step="0.01" value={planLevels[lv]?.[k] ?? ""} onChange={(e) => setPlanLevels((p) => ({ ...p, [lv]: { ...p[lv], [k]: e.target.value } }))} data-testid={`plan-${lv.replace(/\s+/g, "-").toLowerCase()}-${k}`} placeholder="0.00" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pl-3 border-l-2 border-indigo-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-nardo-500">Precios por grado (opcional)</div>
                  <button type="button" onClick={() => addGradeRow(lv)} data-testid={`plan-grade-add-${lv.replace(/\s+/g, "-").toLowerCase()}`} className="text-xs text-navy hover:underline inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar grado</button>
                </div>
                <p className="text-[11px] text-nardo-400 mb-2">Si defines un precio por grado (ej. «1», «2»), tiene prioridad sobre el precio del nivel para los alumnos de ese grado.</p>
                {(planLevels[lv]?.gradeRows || []).map((r) => (
                  <div key={r.gid} className="grid grid-cols-[64px_1fr_1fr_1fr_28px] gap-2 items-end mb-2" data-testid={`plan-grade-row-${lv.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div>
                      <label className="text-[9px] uppercase tracking-[0.1em] text-nardo-400">Grado</label>
                      <input value={r.grade} onChange={(e) => updGradeRow(lv, r.gid, "grade", e.target.value)} placeholder="1" data-testid="plan-grade-name" className="w-full border-b-2 border-nardo-200 bg-transparent py-1.5 outline-none focus:border-navy text-navy text-sm" />
                    </div>
                    {PLAN_KEYS.map((k) => (
                      <div key={k}>
                        <label className="text-[9px] uppercase tracking-[0.1em] text-nardo-400">{PLAN_LABELS[k]}</label>
                        <div className="flex items-center gap-1">
                          <span className="text-nardo-400 text-xs">$</span>
                          <input type="number" min={0} step="0.01" value={r[k]} onChange={(e) => updGradeRow(lv, r.gid, k, e.target.value)} placeholder="0.00" data-testid={`plan-grade-${k}`} className="w-full border-b-2 border-nardo-200 bg-transparent py-1.5 outline-none focus:border-navy text-navy text-sm" />
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => delGradeRow(lv, r.gid)} data-testid="plan-grade-remove" className="text-nardo-400 hover:text-destructive pb-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-nardo-100 pt-5 mt-6">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Becas</div>
            <button type="button" onClick={addScholarship} data-testid="scholarship-add" className="text-xs text-navy hover:underline inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar beca</button>
          </div>
          <p className="text-sm text-nardo-500 mb-3">Define las becas con su porcentaje. Al registrar un alumno podrás elegir una y el % se aplicará automáticamente sobre su colegiatura.</p>
          <div className="space-y-2">
            {scholarships.length === 0 && <p className="text-xs text-nardo-400">Aún no hay becas. Agrega la primera (ej. «Beca hermanos» 20%).</p>}
            {scholarships.map((s) => (
              <div key={s.id} className="flex items-center gap-3" data-testid="scholarship-row">
                <input value={s.name} onChange={(e) => updScholarship(s.id, "name", e.target.value)} placeholder="Nombre de la beca (ej. Beca hermanos)" data-testid="scholarship-name" className="flex-1 border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
                <div className="flex items-center gap-1 w-24">
                  <input type="number" min={0} max={100} step="0.1" value={s.pct} onChange={(e) => updScholarship(s.id, "pct", e.target.value)} placeholder="0" data-testid="scholarship-pct" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm text-right" />
                  <span className="text-nardo-400 text-sm">%</span>
                </div>
                <button type="button" onClick={() => delScholarship(s.id)} data-testid="scholarship-remove" className="text-nardo-400 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <button onClick={savePlans} disabled={savingPlans} data-testid="plans-save" className="mt-5 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
          {savingPlans ? "Guardando…" : "Guardar planes y becas"}
        </button>
      </div>



      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="deposit-accounts-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-navy" /><h2 className="font-display font-semibold text-xl text-navy">Cuentas de depósito</h2></div>
        <p className="text-sm text-nardo-500 mb-5">Estas cuentas se muestran en el Portal de Padres para pagos por transferencia. Solo tú (Director) las controlas.</p>
        <div className="space-y-4">
          {accounts.map((a) => (
            <div key={a.id} className="border border-nardo-200 rounded-lg p-4 grid sm:grid-cols-2 gap-3 relative" data-testid="account-row">
              <input value={a.bank} onChange={(e) => updAccount(a.id, "bank", e.target.value)} placeholder="Banco (ej. BBVA)" data-testid="account-bank" className="border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm" />
              <input value={a.holder} onChange={(e) => updAccount(a.id, "holder", e.target.value)} placeholder="Titular de la cuenta" className="border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm" />
              <input value={a.clabe} onChange={(e) => updAccount(a.id, "clabe", e.target.value)} placeholder="CLABE (18 dígitos)" inputMode="numeric" data-testid="account-clabe" className="border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm font-mono" />
              <input value={a.account} onChange={(e) => updAccount(a.id, "account", e.target.value)} placeholder="No. de cuenta (opcional)" className="border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm font-mono" />
              <input value={a.reference} onChange={(e) => updAccount(a.id, "reference", e.target.value)} placeholder="Referencia / concepto sugerido (opcional)" className="border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm sm:col-span-2" />
              <button type="button" onClick={() => delAccount(a.id)} data-testid="account-remove" className="absolute -top-2 -right-2 bg-white border border-nardo-300 text-nardo-500 hover:text-destructive hover:border-destructive rounded-full p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-nardo-100">
          <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Monto máximo por pago en el Portal de Padres (0 = sin límite)</label>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-nardo-400 text-sm">$</span>
            <input value={maxPay} onChange={(e) => setMaxPay(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" inputMode="decimal" data-testid="max-payment-amount" className="w-40 border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm font-mono" />
          </div>
          <p className="text-[11px] text-nardo-400 mt-1">Límite del monto que un tutor puede capturar al subir su comprobante de transferencia.</p>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={addAccount} data-testid="add-account" className="inline-flex items-center gap-1.5 border border-nardo-300 text-navy hover:border-navy px-4 py-2 rounded-md text-sm font-medium transition-colors"><Plus className="w-4 h-4" /> Agregar cuenta</button>
          <button type="button" onClick={saveAccounts} disabled={savingPay} data-testid="save-accounts" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">{savingPay ? "Guardando…" : "Guardar cuentas"}</button>
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mt-6" data-testid="fee-concepts-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex items-center gap-2 mb-1"><DollarSign className="w-5 h-5 text-navy" /><h2 className="font-display font-semibold text-xl text-navy">Precios por concepto</h2></div>
        <p className="text-sm text-nardo-500 mb-5">Define precios fijos por concepto. En el Portal de Padres el monto se autocompleta. El administrador (staff) no puede modificarlos.</p>
        <div className="space-y-3">
          {concepts.map((c) => (
            <div key={c.id} className="flex items-center gap-3" data-testid="concept-row">
              <input value={c.name} onChange={(e) => updConcept(c.id, "name", e.target.value)} placeholder="Concepto (ej. Transporte)" data-testid="concept-name" className="flex-1 border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm" />
              <div className="flex items-center gap-1">
                <span className="text-nardo-400 text-sm">$</span>
                <input value={c.amount} onChange={(e) => updConcept(c.id, "amount", e.target.value)} placeholder="0.00" inputMode="decimal" data-testid="concept-amount" className="w-28 border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy text-sm font-mono" />
              </div>
              <button type="button" onClick={() => delConcept(c.id)} data-testid="concept-remove" className="text-nardo-400 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {concepts.length === 0 && <p className="text-[12px] text-nardo-400">Aún no has agregado conceptos.</p>}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={addConcept} data-testid="add-concept" className="inline-flex items-center gap-1.5 border border-nardo-300 text-navy hover:border-navy px-4 py-2 rounded-md text-sm font-medium transition-colors"><Plus className="w-4 h-4" /> Agregar concepto</button>
          <button type="button" onClick={saveConcepts} disabled={savingPay} data-testid="save-concepts" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-5 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50">{savingPay ? "Guardando…" : "Guardar precios"}</button>
        </div>
      </div>

      </>)}

      {section === "padres" && (<>
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative" data-testid="parent-code-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Acceso para padres / tutores</h2>
        </div>
        <p className="text-sm text-nardo-500 mb-5">Los tutores entran al <strong>Portal de Padres</strong> usando únicamente el <strong>UID</strong> del alumno (el de su tarjeta/credencial NFC). No se requiere ningún otro código y el UID no cambia, así que no tienen que volver a vincularse.</p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-mono text-sm text-navy bg-nardo-50 border-2 border-dashed border-nardo-300 px-5 py-3 rounded-lg" data-testid="parent-portal-url">{`${window.location.origin}/padres`}</div>
          <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/padres`); toast.success("Enlace copiado"); }} data-testid="parent-portal-copy" className="inline-flex items-center gap-1.5 border border-nardo-300 text-nardo-600 hover:border-navy hover:text-navy px-4 py-2.5 rounded-md text-sm font-medium transition-colors"><Copy className="w-4 h-4" /> Copiar enlace</button>
        </div>
        <p className="text-[11px] text-nardo-400 mt-3">Comparte este enlace con los padres. Cada alumno se consulta con su UID; un tutor puede agregar a varios hijos.</p>
      </div>

      </>)}

      {section === "staff" && (
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <StaffManager />
      </div>
      )}
    </div>
  );
}
